import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, AlertCircle, AlertOctagon, ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { fetchTraegerDashboard } from "./traeger-api";
import {
  ChildStatusChips,
  RenewLicenseCTA,
  childDocState,
  childLicenseState,
} from "./child-status";

/**
 * Träger-Dashboard — übersicht über alle child-orgs.
 *
 * Zeigt: traeger-header, status-cards pro child (B11: ZWEI getrennte
 * status-dimensionen — Lizenz + Doku — statt eines vermischten „rot",
 * heat-stats der letzten 30 tage), quick-actions (orgs verwalten + export).
 *
 * B11-historie: vorher kollabierte ein einzelner compliance-chip (green/
 * yellow/red) lizenz-sperre und doku-lücke in denselben roten zustand. Der
 * Träger-Admin konnte „Lizenz verlängern" nicht von „Doku mit Einrichtung
 * klären" unterscheiden. Jetzt: zwei chips + zustands-spezifischer CTA.
 * Status-logik + chips: ./child-status.tsx.
 */

export function TraegerDashboardRoute(): React.ReactElement {
  const dashboard = useQuery({
    queryKey: ["traeger", "dashboard"],
    queryFn: fetchTraegerDashboard,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  if (dashboard.isPending) {
    return <DashboardSkeleton />;
  }
  if (dashboard.isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        Fehler beim Laden des Träger-Dashboards.
      </div>
    );
  }

  const data = dashboard.data;
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{data.traeger_name}</h1>
          <p className="text-sm text-neutral-600">
            {data.child_count} von {data.max_children} Einrichtungen · {data.period_label}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/traeger/orgs"
            className="rounded-md bg-[color:var(--color-warm-orange-700)] px-3 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-warm-orange-800)]"
          >
            Einrichtungen verwalten
          </Link>
          <Link
            to="/traeger/export"
            className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-50"
          >
            Export
          </Link>
        </div>
      </header>

      {data.child_count < data.min_children && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          <strong className="block">Hinweis:</strong>
          Mindest-Anzahl Einrichtungen für T-Tier-Pricing ist {data.min_children}.
          Aktuell verwaltet: {data.child_count}.
        </div>
      )}

      <section aria-label="Einrichtungen">
        {data.child_summary.length === 0 ? (
          <EmptyChildList />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.child_summary.map((c) => (
              <ChildCard key={c.org_id} child={c} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ChildCard({
  child,
}: {
  child: import("./traeger-api").TraegerChildSummary;
}): React.ReactElement {
  const licenseState = childLicenseState(child);
  const docState = childDocState(child);
  const licenseExpired = licenseState === "expired";
  const docNeedsAttention = docState !== "complete";

  return (
    <article className="flex flex-col rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <header className="mb-3 flex flex-col gap-2">
        <h3 className="text-base font-semibold text-neutral-900">{child.org_name}</h3>
        {/* B11: zwei ORTHOGONALE status-dimensionen, nie zu einem rot vermischt */}
        <ChildStatusChips child={child} />
      </header>

      <dl className="grid grid-cols-3 gap-2 text-sm">
        <Stat label="Max innen" value={`${child.max_indoor_temp_c.toFixed(1)} °C`} />
        <Stat label="Einträge" value={String(child.total_entries)} />
        <Stat label="Tage" value={String(child.distinct_days)} />
        {/* Stufen-labels: 1=Soll, 2=Muss, 3=Kritisch (stufen-labels.ts) */}
        <Stat label="Soll (1)" value={String(child.soll_count)} />
        <Stat label="Muss (2)" value={String(child.muss_count)} />
        <Stat label="Kritisch (3)" value={String(child.critical_count)} />
      </dl>

      {/*
       * Zustands-spezifische action-zone — eindeutig getrennt nach dimension:
       *   1. Lizenz abgelaufen  → eigener block + CTA "Lizenz verlängern"
       *   2. aktive Lizenz, aber Doku-lücke → anderer, klar unterscheidbarer hinweis
       * Beide können gleichzeitig sichtbar sein (sind unabhängig).
       */}
      {licenseExpired && (
        <div className="mt-3 rounded-md border border-[color:var(--color-heat-4)] bg-[color:var(--color-heat-4-soft)] p-3">
          <p className="flex items-start gap-1.5 text-xs text-[color:var(--color-heat-4)]">
            <AlertOctagon aria-hidden="true" className="mt-0.5 size-4 flex-shrink-0" />
            <span>
              <strong className="font-semibold">Lizenz abgelaufen.</strong> Neue
              Einträge sind für diese Einrichtung gesperrt (Lesezugriff bleibt).
              Dies ist kein Doku-Problem.
            </span>
          </p>
          <RenewLicenseCTA child={child} className="mt-2 w-full" />
        </div>
      )}

      {!licenseExpired && docNeedsAttention && (
        <p className="mt-3 flex items-start gap-1.5 rounded-md border border-[color:var(--color-heat-3)] bg-[color:var(--color-heat-3-soft)] p-3 text-xs text-[color:var(--color-heat-3)]">
          <AlertCircle aria-hidden="true" className="mt-0.5 size-4 flex-shrink-0" />
          <span>
            <strong className="font-semibold">Doku-Lücke trotz aktiver Lizenz.</strong>{" "}
            Empfohlene Doku-Dichte für die erreichte Hitze-Stufe unterschritten —
            bitte mit dem Einrichtungs-Admin klären (keine Lizenz-Aktion nötig).
          </span>
        </p>
      )}
    </article>
  );
}

function Stat({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="font-semibold text-neutral-900">{value}</dd>
    </div>
  );
}

function EmptyChildList(): React.ReactElement {
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center">
      <Building2 className="mx-auto mb-3 size-8 text-neutral-400" />
      <h3 className="text-base font-semibold text-neutral-900">
        Noch keine Einrichtungen angelegt
      </h3>
      <p className="mt-1 text-sm text-neutral-600">
        Legen Sie Ihre erste Einrichtung an, um das Dashboard zu nutzen.
      </p>
      <Link
        to="/traeger/orgs"
        className="mt-4 inline-flex items-center gap-1 rounded-md bg-[color:var(--color-warm-orange-700)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-warm-orange-800)]"
      >
        Einrichtung anlegen
        <ChevronRight className="size-4" />
      </Link>
    </div>
  );
}

function DashboardSkeleton(): React.ReactElement {
  return (
    <div className="space-y-6">
      <div className="h-8 w-1/3 animate-pulse rounded bg-neutral-200" />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-44 animate-pulse rounded-lg bg-neutral-100" />
        ))}
      </div>
    </div>
  );
}
